import { Recipient } from '@weave/schema-gen-ts/dist/schemas/messaging/etl/bulk/v1/service.pb.js';
import { DestinationType_Enum } from '@weave/schema-gen-ts/dist/schemas/messaging/shared/v1/enums.pb';
import { UseInfiniteQueryResult } from 'react-query';
import { InfinitePaginatedList } from '@frontend/components';
import { useGetDisplayName } from '@frontend/get-display-name';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Button, ListRow, ModalControlModalProps, phone } from '@frontend/design-system';
import { BulkMessagingModal } from './bulk-messaging-modal';

type RecipientListItemProps = Pick<
  Recipient,
  'preferredName' | 'firstName' | 'lastName' | 'destination' | 'destinationType'
>;

const RecipientListItem = ({
  destination,
  destinationType,
  firstName = '',
  lastName = '',
  preferredName = '',
}: RecipientListItemProps) => {
  const { fullName } = useGetDisplayName({
    person: {
      firstName: firstName || '',
      lastName: lastName || '',
      preferredName: preferredName || '',
    },
  });

  return (
    <ListRow>
      <ListRow.Content>
        <ListRow.Content.Title>{fullName}</ListRow.Content.Title>
        {destination ? (
          <ListRow.Content.Subtitle>
            {destinationType === DestinationType_Enum.SMS
              ? phone(destination, { format: 'standard', hideCountry: true })
              : destination}
          </ListRow.Content.Subtitle>
        ) : null}
      </ListRow.Content>
    </ListRow>
  );
};

type RecipientModalProps = ModalControlModalProps & {
  recipients: UseInfiniteQueryResult<{ rows: Recipient[] }, unknown>;
  title: string;
  trackingIdPrefix: string;
};

export const RecipientModal = ({ onClose, recipients, title, trackingIdPrefix, ...rest }: RecipientModalProps) => {
  const { t } = useTranslation('bulk-messaging');
  const modalTrackingIdPrefix = `${trackingIdPrefix}-recipient-modal`;

  return (
    <BulkMessagingModal
      {...rest}
      bodyStyles={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        msOverflowStyle: 'none',
        paddingLeft: `${theme.spacing(5)} !important`,
        paddingRight: `${theme.spacing(5)} !important`,
        scrollbarWidth: 'none',
        '::-webkit-scrollbar': {
          display: 'none',
        },
      }}
      css={{
        minHeight: 530,
      }}
      onClose={onClose}
      title={title}
      trackingIdPrefix={modalTrackingIdPrefix}
    >
      <BulkMessagingModal.RecipientsContainer>
        <InfinitePaginatedList
          infiniteQueryProps={recipients}
          renderListItem={({ listItem: recipient }) => <RecipientListItem {...recipient} key={recipient.destination} />}
        />
      </BulkMessagingModal.RecipientsContainer>
      <BulkMessagingModal.ButtonContainer>
        <Button onClick={onClose} size='small' trackingId={`${modalTrackingIdPrefix}-close-button`} variant='secondary'>
          {t('Close')}
        </Button>
      </BulkMessagingModal.ButtonContainer>
    </BulkMessagingModal>
  );
};
